<template>
  <div>
    <Dialog
      :visible.sync="localShow"
      :modal="true"
      :closable="true"
      :style="{ width: $isMobile() ? '100vw' : '70vw' }"
      v-bind:class="[{ 'p-dialog-maximized': $isMobile() }]"
    >
      <template #header>
        <div class="dialog-header-wrap">
          <span class="p-dialog-title">Localizações do Dia</span>
          <span v-if="calculatingDistance" class="p-tag p-tag-warning distance-tag">
            <i class="pi pi-spin pi-spinner p-mr-1"></i> A calcular km...
          </span>
          <span
            v-else-if="totalDistanceKm !== null"
            class="p-tag p-tag-info distance-tag"
            :title="distanceTitle"
          >
            <i class="pi pi-car p-mr-1"></i>
            Total aprox. {{ totalDistanceKm }} km
            <small class="base-badge-note">
              ({{ baseLocation.name }} ⇄ Base{{ distanceIsEstimate ? ", estimativa" : "" }}{{ missingCoordsCount ? `, ${missingCoordsCount} sem coordenadas` : "" }})
            </small>
          </span>
        </div>
      </template>

      <div class="locations-content">
        <div class="locations-map-container" v-if="mappableLocations.length > 0">
          <l-map
            :zoom="zoom"
            :center="center"
            :options="mapOptions"
            style="height: 100%; width: 100%"
            ref="locationsMap"
            :key="mapKey"
          >
            <l-tile-layer :url="tileUrl" :attribution="attribution" />

            <l-polyline
              v-if="routePolyline.length > 0"
              :lat-lngs="routePolyline"
              :color="distanceIsEstimate ? '#64748b' : '#2563eb'"
              :weight="4"
              :opacity="0.8"
              :dashArray="distanceIsEstimate ? '6, 8' : null"
            />

            <!-- Marcador da Base (Partida / Regresso) -->
            <l-marker
              v-if="baseLocation && baseLocation.latLng"
              :lat-lng="baseLocation.latLng"
              :icon="createBaseMarkerIcon()"
            >
              <l-popup>
                <div><strong>🏢 Base (Partida / Regresso)</strong></div>
                <div><b>{{ baseLocation.name }}</b></div>
                <div v-if="baseLocation.address">{{ baseLocation.address }}</div>
              </l-popup>
            </l-marker>

            <l-marker
              v-for="(loc, index) in mappableLocations"
              :key="index"
              :lat-lng="loc.latLng"
              :icon="createMarkerIcon(loc.order)"
            >
              <l-popup>
                <div><b>PAT:</b> {{ loc.pat_number }}</div>
                <div><b>Cliente:</b> {{ loc.contact_name }}</div>
                <div><b>Morada:</b> {{ loc.address }}</div>
                <div v-if="loc.zip_code"><b>Cód. Postal:</b> {{ loc.zip_code }}</div>
                <div v-if="loc.city"><b>Cidade:</b> {{ loc.city }}</div>
                <div v-if="loc.hour"><b>Hora:</b> {{ loc.hour }}</div>
                <div class="p-mt-2">
                  <Button
                    label="Ver Pat"
                    icon="pi pi-eye"
                    class="p-button-sm p-button-success"
                    @click="goToPat(loc.pat_number)"
                  />
                </div>
              </l-popup>
            </l-marker>
          </l-map>
        </div>

        <div v-if="loading" class="locations-placeholder">
          <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
        </div>

        <div class="locations-list" v-if="!loading">
          <!-- Item da Base no início da lista -->
          <div
            v-if="baseLocation && mappableLocations.length > 0"
            class="location-item location-item-clickable location-item-base"
            @click="focusMarker({ latLng: baseLocation.latLng })"
          >
            <div class="location-index location-index-base">
              <i class="pi pi-home" style="font-size: 0.85rem"></i>
            </div>
            <div class="location-details">
              <div>
                <strong>Base · {{ baseLocation.name }}</strong>
                <span class="p-badge p-ml-2 p-badge-info">Partida & Regresso</span>
              </div>
              <small class="p-text-secondary">
                {{ baseLocation.address }}
              </small>
            </div>
          </div>

          <div
            v-for="(loc, index) in allLocations"
            :key="index"
            class="location-item"
            :class="{ 'location-item-clickable': loc.hasCoords }"
            @click="loc.hasCoords ? focusMarker(loc) : null"
          >
            <div class="location-index" :class="{ 'location-index-disabled': !loc.hasCoords }">
              {{ index + 1 }}
            </div>
            <div class="location-details">
              <div>
                <strong>PAT {{ loc.pat_number }}</strong>
                <span v-if="loc.hour" class="p-ml-2 p-text-secondary">{{ loc.hour }}</span>
                <span class="p-badge p-ml-2" :style="{ backgroundColor: loc.color_status, color: getTextColor(loc.color_status) }">
                  {{ loc.pat_status }}
                </span>
              </div>
              <div class="p-text-secondary">{{ loc.contact_name }}</div>
              <small class="p-text-secondary">
                {{ loc.address }}
                <span v-if="loc.zip_code"> · {{ loc.zip_code }}</span>
              </small>
            </div>
          </div>
          <div v-if="allLocations.length === 0" class="p-text-center p-p-3 p-text-secondary">
            Nenhuma intervenção encontrada para este dia.
          </div>
        </div>
      </div>
      <template #footer>
        <Button
          label="Fechar"
          icon="pi pi-times"
          class="p-button-text"
          @click="close"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { latLng, divIcon } from "leaflet";
import { LMap, LTileLayer, LMarker, LPopup, LPolyline } from "vue2-leaflet";
import ProgressSpinner from "primevue/progressspinner";
import chroma from "chroma-js";
import { getBaseLocation, getDayTasks, calculateRoundTrip, hasCoords } from "@/helpers/routeDistance";

export default {
  name: "ShowAllLocations",
  components: { LMap, LTileLayer, LMarker, LPopup, LPolyline, ProgressSpinner },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    currentDate: {
      type: String,
      default: "",
    },
    userId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      localShow: false,
      loading: false,
      calculatingDistance: false,
      totalDistanceKm: null,
      distanceIsEstimate: false,
      routePolyline: [],
      requestSequence: 0,
      allLocations: [],
      zoom: 7,
      center: latLng(39.2895048, -8.6267249),
      mapOptions: {
        zoomSnap: 0.5,
      },
      mapKey: 0,
      tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution:
        '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    };
  },
  computed: {
    mappableLocations() {
      return this.allLocations.filter((loc) => loc.hasCoords);
    },
    missingCoordsCount() {
      return this.allLocations.length - this.mappableLocations.length;
    },
    baseLocation() {
      const user = this.$store && this.$store.state && this.$store.state.auth && this.$store.state.auth.user;
      const base = getBaseLocation(user);
      return base ? { ...base, latLng: latLng(base.lat, base.lng) } : null;
    },
    distanceTitle() {
      const how = this.distanceIsEstimate
        ? "Estimativa em linha reta x1.3 (rota indisponível)"
        : "Distância por estrada";
      const missing = this.missingCoordsCount
        ? ` · ${this.missingCoordsCount} intervenção(ões) sem coordenadas não incluída(s)`
        : "";
      return `${how} · Partida e regresso: ${this.baseLocation ? this.baseLocation.name : ""}${missing}`;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        this.localShow = newVal;
        if (newVal) {
          this.onDialogOpen();
        }
      },
    },
    currentDate(newVal) {
      if (this.localShow && newVal) {
        this.onDialogOpen();
      }
    },
    localShow(newVal) {
      if (!newVal && this.show) {
        this.$emit("close");
      }
    },
  },
  methods: {
    async onDialogOpen() {
      const sequence = ++this.requestSequence;
      this.loading = true;
      this.allLocations = [];
      this.totalDistanceKm = null;
      this.distanceIsEstimate = false;
      this.routePolyline = [];
      this.calculatingDistance = false;
      this.mapKey++;

      try {
        const tasks = await getDayTasks(this.userId, this.currentDate);
        if (sequence !== this.requestSequence) return;

        let order = 1;
        this.allLocations = tasks.map((task) => {
          const loc = task.localization || {};
          const withCoords = hasCoords(loc);
          return {
            pat_number: task.pat_number,
            contact_name: task.contact_name || task.entity_name || "",
            address: loc.address || "",
            zip_code: loc.zip_code || "",
            city: loc.city || "",
            hour: this.extractHour(task.start),
            pat_status: task.pat_status || "",
            color_status: task.color_status || "#cccccc",
            hasCoords: withCoords,
            order: withCoords ? order++ : null,
            latLng: withCoords ? latLng(loc.latitude, loc.longitude) : null,
          };
        });

        this.$nextTick(() => this.fitBounds());
        this.calculateTotalKms(sequence);
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Não foi possível carregar as localizações.",
          life: 3000,
        });
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },

    async calculateTotalKms(sequence) {
      // Full route: Base -> Intervention 1 -> ... -> Intervention N -> Base
      if (!this.baseLocation || this.mappableLocations.length === 0) return;

      this.calculatingDistance = true;
      try {
        const result = await calculateRoundTrip(
          this.baseLocation,
          this.mappableLocations.map((loc) => loc.latLng)
        );
        if (sequence !== this.requestSequence) return;
        this.totalDistanceKm = result.km;
        this.distanceIsEstimate = result.isEstimate;
        this.routePolyline = result.polyline;
      } finally {
        if (sequence === this.requestSequence) this.calculatingDistance = false;
      }
    },

    extractHour(dateStr) {
      if (!dateStr) return "";
      const parts = dateStr.split(" ");
      return parts.length > 1 ? parts[1] : "";
    },

    fitBounds() {
      if (this.$refs.locationsMap && this.mappableLocations.length > 0) {
        const map = this.$refs.locationsMap.mapObject;
        const allBounds = this.mappableLocations.map((loc) => [loc.latLng.lat, loc.latLng.lng]);
        if (this.baseLocation) {
          allBounds.push([this.baseLocation.lat, this.baseLocation.lng]);
        }
        map.fitBounds(allBounds, { padding: [40, 40], maxZoom: 14 });
      }
    },

    createBaseMarkerIcon() {
      const svgIcon = `
        <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
          <path fill="#0284c7" stroke="#fff" stroke-width="1.5" d="M16,0C7.2,0,0,7.2,0,16c0,10.1,16,26,16,26s16-15.9,16-26C32,7.2,24.8,0,16,0z"/>
          <circle fill="#fff" cx="16" cy="15" r="9"/>
          <path fill="#0284c7" d="M16 8.5 L9.5 14 L11 15.2 L12 14.3 V20 H15 V16 H17 V20 H20 V14.3 L21 15.2 L22.5 14 Z"/>
        </svg>
      `;

      return divIcon({
        html: svgIcon,
        className: "location-marker-icon",
        iconSize: [32, 42],
        iconAnchor: [16, 42],
        popupAnchor: [0, -36],
      });
    },

    createMarkerIcon(number) {
      const svgIcon = `
        <svg width="30" height="42" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg">
          <path fill="#E53935" stroke="#fff" stroke-width="1.5" d="M15,0C6.7,0,0,6.7,0,15c0,9.4,15,27,15,27s15-17.6,15-27C30,6.7,23.3,0,15,0z"/>
          <circle fill="#fff" cx="15" cy="14" r="10"/>
          <text x="15" y="18" text-anchor="middle" fill="#E53935" font-size="12" font-weight="bold">${number}</text>
        </svg>
      `;

      return divIcon({
        html: svgIcon,
        className: "location-marker-icon",
        iconSize: [30, 42],
        iconAnchor: [15, 42],
        popupAnchor: [0, -34],
      });
    },

    focusMarker(loc) {
      if (this.$refs.locationsMap && loc.latLng) {
        const map = this.$refs.locationsMap.mapObject;
        map.setView(loc.latLng, 14, { animate: true });
      }
    },

    getTextColor(bgColor) {
      if (!bgColor) return "#000";
      try {
        const whiteContrast = chroma.contrast(bgColor, "#ffffff");
        const blackContrast = chroma.contrast(bgColor, "#000000");
        return whiteContrast > blackContrast ? "#ffffff" : "#000000";
      } catch {
        return "#000";
      }
    },

    goToPat(patNumber) {
      this.$router.push(`/pats/${patNumber}`);
    },

    close() {
      this.localShow = false;
    },
  },
};
</script>

<style scoped>
.locations-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.locations-map-container {
  width: 100%;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dee2e6;
}

.locations-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  color: #6c757d;
}

.locations-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

.location-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s;
}

.location-item:last-child {
  border-bottom: none;
}

.location-item-clickable {
  cursor: pointer;
}

.location-item-clickable:hover {
  background-color: #f0f9ff;
}

.location-index {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  background: #E53935;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.85rem;
  margin-right: 0.75rem;
}

.location-index-disabled {
  background: #adb5bd;
}

.location-details {
  flex: 1;
  min-width: 0;
}

.location-details small {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.location-marker-icon) {
  background: none !important;
  border: none !important;
}

.dialog-header-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.distance-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.25rem 0.65rem;
  border-radius: 4px;
}

.base-badge-note {
  margin-left: 0.35rem;
  font-weight: normal;
  opacity: 0.9;
}

.location-item-base {
  background-color: #f8fafc;
  border-left: 3px solid #0284c7;
}

.location-index-base {
  background: #0284c7;
}
</style>
